import { ReactNode, useMemo } from 'react';
import { ChartRenderer } from 'components/core';
import { normalizeChartType, RENDERABLE_CHART_TYPES } from 'constants/appConstants';
import { PreviewSource, buildPreviewRequest, allTimeRange, formatMetric, previewToChart } from 'helpers/kpiBuilder/previewRequest';
import { useLivePreview } from './hooks/useLivePreview';
import { ClippedChart } from './ClippedChart';
import { PreviewCard } from './PreviewCard';

// Tab views are drawn from several queries; they keep the sample until the backend's tab format is wired in.
const LIVE_TYPES = RENDERABLE_CHART_TYPES.filter((t) => t !== 'tabView');

interface LivePreviewProps {
  source: PreviewSource;
  chartType: string;
  title: string;
  subTitle: string;
  /** Shown while there is nothing to run yet, and for chart types that cannot be drawn from real data. */
  fallback: ReactNode;
}

// The visualization as it will look on a dashboard, drawn from all the real data.
export const LivePreview = ({ source, chartType, title, subTitle, fallback }: LivePreviewProps) => {
  const { code, queries, config, handlerKey } = source;
  const request = useMemo(
    () => buildPreviewRequest({ code, queries, config, handlerKey }, allTimeRange()),
    [code, queries, config, handlerKey]
  );
  const { loading, error, response } = useLivePreview(request);
  const type = normalizeChartType(chartType);
  const chart = response && LIVE_TYPES.includes(type) ? previewToChart(type, response) : null;
  const drawn = chart?.metric ? <ChartRenderer chartType="metric" chartData={{ title, count: formatMetric(chart.metric), insight: chart.metric.insight }} /> : chart?.data && <ClippedChart chartType={type} data={chart.data} />;

  return (
    <div>
      <div className="small fw-bold mb-2">{loading ? 'Loading real data…' : 'Preview with real data'}</div>
      {error && <div className="alert alert-danger small py-2" role="alert">{error}</div>}
      {chart?.errors.map((e) => <div key={e} className="alert alert-warning small py-2" role="alert">{e}</div>)}
      {chart?.warnings.map((w) => <div key={w} className="small text-secondary mb-1">{w}</div>)}
      {drawn ? <PreviewCard title={title} subTitle={subTitle} tile={!!chart?.metric}>{drawn}</PreviewCard> : fallback}
    </div>
  );
};
